// Treats: F2P currency earned from Story clears and Endless Score.
// Spent in the Store Treats tab. Treats cannot be purchased.

import { getStoryTreatsForLevelClear } from "../data/storyLevelRewards";
import {
    buildEndlessScoreModifiersFromRunConfig,
    getEndlessTreatsFromScore,
} from "./endlessScore";
import {
    BOOST_BUNDLE_SHOP_ID,
    BOOST_BUNDLE_TREAT_COST,
    BOOST_CATALOG,
    BOOST_TREAT_COSTS,
} from "./boosts";

export const TREATS_STORAGE_CAP = 9999900;
export const CORGI_PACK_COST = 2500;
export const CORGI_PACK_SIZE = 5;
export const CORGI_PACK_DUPLICATE_REFUND = 100;
export const SPECIAL_CORGI_PACK_DUPLICATE_REFUND = 2500;
export const CORGI_PACK_BUNDLE_COUNTS = [1, 3, 5, 10];
export const TREATS_HEART_COST = 1000;
export const ADMIN_TREATS_GRANT = 10000;

// Story earn rates are per-level; Endless converts Score → Treats.
export const TREATS_EARN_RATES_TBD = false;
export const TREATS_STORY_CLEAR = 500;
/** @deprecated Endless pays from Score now; kept for older references. */
export const TREATS_ENDLESS_PER_LEVEL = 300;
export const TREATS_EARN_RATES_TBD_COPY =
    "Endless Treats convert from Score at board-size rates (2×2 33%, 3×3 66%, 4×4 100%). Completed boards pay full rate; the unfinished board when the run ends pays 50%. Story pays per-level Treats and/or Corgi Packs (see docs/story-level-rewards.md).";

export const TREATS_SHOP_KINDS = {
    HEARTS: "hearts",
    PACKS: "packs",
    BOOSTS: "boosts",
};

export function clampTreats(amount, cap = TREATS_STORAGE_CAP) {
    return Math.max(0, Math.min(cap, Math.floor(Number(amount) || 0)));
}

export function grantTreats(current = 0, amount = 0, cap = TREATS_STORAGE_CAP) {
    const add = Math.max(0, Math.floor(Number(amount) || 0));
    return clampTreats((Number(current) || 0) + add, cap);
}

export function spendTreats(current = 0, amount = 0, cap = TREATS_STORAGE_CAP) {
    const cost = Math.max(0, Math.floor(Number(amount) || 0));
    const have = clampTreats(current, cap);
    if (have < cost) {
        return { ok: false, treats: have };
    }
    return { ok: true, treats: clampTreats(have - cost, cap) };
}

export function mergeTreats(localAmount = 0, accountAmount = 0) {
    return Math.max(clampTreats(localAmount), clampTreats(accountAmount));
}

export function getEndlessTreatsForRun({
    score = 0,
    levelsCompleted = 0,
    boardSize = 4,
    runConfig = null,
    modifiers = null,
} = {}) {
    const resolvedModifiers =
        modifiers || buildEndlessScoreModifiersFromRunConfig(runConfig);

    return getEndlessTreatsFromScore({
        score,
        levelsCompleted,
        boardSize: boardSize || runConfig?.boardSize || 4,
        modifiers: resolvedModifiers,
    });
}

export function getStoryTreatsForClear(didWin = false, storyLevelId = null) {
    if (!didWin) {
        return 0;
    }

    if (storyLevelId != null && storyLevelId !== "") {
        return getStoryTreatsForLevelClear(storyLevelId, true);
    }

    return TREATS_STORY_CLEAR;
}

export function formatTreats(amount = 0) {
    return `${clampTreats(amount).toLocaleString("en-US")} Treats`;
}

export function normalizeCorgiPackCount(packCount = 1) {
    const count = Math.max(1, Math.floor(Number(packCount) || 1));
    return CORGI_PACK_BUNDLE_COUNTS.includes(count) ? count : 1;
}

/** Any positive pack count for Story / redemption grants (not shop bundles). */
export function clampCorgiPackGrantCount(packCount = 0) {
    return Math.max(0, Math.min(25, Math.floor(Number(packCount) || 0)));
}

export function formatCorgiPackGrantLabel(packCount = 0) {
    const count = clampCorgiPackGrantCount(packCount);
    if (count <= 0) {
        return "";
    }
    return count === 1 ? "1 Corgi Pack" : `${count} Corgi Packs`;
}

export function getCorgiPackCost(packCount = 1) {
    return CORGI_PACK_COST * normalizeCorgiPackCount(packCount);
}

export function listCorgiPackBundles() {
    return CORGI_PACK_BUNDLE_COUNTS.map((packCount) => ({
        packCount,
        cardCount: packCount * CORGI_PACK_SIZE,
        cost: getCorgiPackCost(packCount),
    }));
}

export function formatCorgiPackBundleTitle(packCount = 1) {
    const count = normalizeCorgiPackCount(packCount);
    return count === 1 ? "Corgi Pack" : `${count} Corgi Packs`;
}

export function listTreatsShopItems() {
    const boostItems = BOOST_CATALOG.map((boost) => ({
        id: `treats_shop_${boost.id}`,
        kind: TREATS_SHOP_KINDS.BOOSTS,
        title: boost.name,
        description: `${boost.description} Added to Inventory for use during Story and Endless runs.`,
        cost: BOOST_TREAT_COSTS[boost.id],
        hearts: 0,
        packs: 0,
        boostItemId: boost.id,
        boostAmount: 1,
    }));

    return [
        {
            id: "treats_shop_heart_1",
            kind: TREATS_SHOP_KINDS.HEARTS,
            title: "+1 Heart",
            description:
                "Add one heart to your stash. Can go above the natural 3-heart regen limit.",
            cost: TREATS_HEART_COST,
            hearts: 1,
            packs: 0,
        },
        {
            id: "treats_shop_heart_3",
            kind: TREATS_SHOP_KINDS.HEARTS,
            title: "+3 Hearts",
            description:
                "Add three hearts to your stash. Can go above the natural 3-heart regen limit.",
            cost: TREATS_HEART_COST * 3,
            hearts: 3,
            packs: 0,
        },
        {
            id: "treats_shop_pack_1",
            kind: TREATS_SHOP_KINDS.PACKS,
            title: "Corgi Packs",
            description: `Choose how many packs to buy. ${CORGI_PACK_SIZE} cards each, same odds as an Endless board. Added to Inventory. Duplicates refund ${CORGI_PACK_DUPLICATE_REFUND.toLocaleString("en-US")} Treats; Special Corgi duplicates refund ${SPECIAL_CORGI_PACK_DUPLICATE_REFUND.toLocaleString("en-US")} Treats.`,
            cost: CORGI_PACK_COST,
            hearts: 0,
            packs: 1,
            askQuantity: true,
        },
        {
            id: BOOST_BUNDLE_SHOP_ID,
            kind: TREATS_SHOP_KINDS.BOOSTS,
            title: "Boost Bundle",
            description:
                "1 Reveal + 1 Shield + 1 Freeze. Added to Inventory. Slightly cheaper than buying each alone.",
            cost: BOOST_BUNDLE_TREAT_COST,
            hearts: 0,
            packs: 0,
            boostBundle: 1,
        },
        ...boostItems,
    ];
}

export function getTreatsShopItemById(itemId) {
    if (!itemId) {
        return null;
    }
    return (
        listTreatsShopItems().find((item) => item.id === String(itemId)) || null
    );
}
